import React, { useRef, useEffect } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'

// Hooks
import { useScrolledAnimation } from '../useScrolledAnimation'

// Styles
import { AnimateScaleInWrapper, AnimateScaleInInner } from './index.style'

const AnimateScaleIn = ({
  id,
  duration,
  delay,
  scaled,
  position,
  borderRadius,
  pointerEvents,
  syncWithScroll,
  useInView,
  isInView,
  debug,
  children,
}) => {
  const innerRef = useRef(null)
  const scaleIn = useRef(null)

  useScrolledAnimation({
    id: id,
    transition: scaleIn,
    sync: syncWithScroll,
    delay: delay,
    useInView: useInView,
    isInView: isInView,
    debug: debug,
  })

  useEffect(() => {
    if (!innerRef.current) return

    scaleIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: duration,
        ease: syncWithScroll ? 'power1.out' : 'power2.inOut',
      },
    })

    scaleIn.current.fromTo(
      innerRef.current,
      {
        y: '10%',
        scale: scaled,
      },
      {
        y: 0,
        scale: 1.0001,
      },
      0
    )
  }, [innerRef, scaled, duration, syncWithScroll])

  return (
    <AnimateScaleInWrapper
      borderRadius={borderRadius}
      pointer={pointerEvents}
      data-scroll
      data-scroll-id={id}
    >
      <AnimateScaleInInner ref={innerRef} position={position}>
        {children}
      </AnimateScaleInInner>
    </AnimateScaleInWrapper>
  )
}

AnimateScaleIn.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  duration: PropTypes.number,
  scaled: PropTypes.number,
  borderRadius: PropTypes.bool,
  position: PropTypes.oneOf(['absolute', 'relative']),
  pointerEvents: PropTypes.oneOf(['none', '']),
  useInView: PropTypes.bool,
  isInView: PropTypes.bool,
  syncWithScroll: PropTypes.bool,
}

AnimateScaleIn.defaultProps = {
  delay: 0,
  duration: 1.4,
  scaled: 1.3,
  position: 'absolute',
  pointerEvents: 'none',
  useInView: true,
  isInView: false,
  syncWithScroll: true,
}

export default AnimateScaleIn
